import React, { useState, useEffect } from 'react';
import EmployeeList from './EmployeeList';
import AddEmployee from './AddEmployee';
import EmployeeFeedback from './EmployeeFeedback';

const EmployeeManagement = ({ userRole }) => {
  const [activeTab, setActiveTab] = useState('list');
  const [employees, setEmployees] = useState([]);
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadEmployees();
    loadFeedbacks();
  }, []);

  const loadEmployees = () => {
    const savedEmployees = localStorage.getItem('employees');
    if (savedEmployees) {
      setEmployees(JSON.parse(savedEmployees));
    } else {
      // Sample employees data
      const sampleEmployees = [
        {
          id: 1,
          employeeId: 'EMP001',
          name: 'Harsh Sharma',
          email: 'harsh@aizops.com',
          phone: '+91 98765 43210',
          designation: 'Senior Software Developer',
          department: 'Engineering',
          joiningDate: '2023-01-15',
          role: 'employee',
          status: 'active',
          salary: 850000,
          address: '123 Main St, Mumbai',
          emergencyContact: '+91 98765 43211',
          bloodGroup: 'O+',
          dateOfBirth: '1995-03-15'
        },
        {
          id: 2,
          employeeId: 'EMP002',
          name: 'Aman Verma',
          email: 'aman@aizops.com',
          phone: '+91 98765 43211',
          designation: 'IT Manager',
          department: 'IT',
          joiningDate: '2022-06-10',
          role: 'manager',
          status: 'active',
          salary: 1200000,
          address: '456 Park Street, Delhi',
          emergencyContact: '+91 98765 43212',
          bloodGroup: 'B+',
          dateOfBirth: '1992-07-22'
        },
        {
          id: 3,
          employeeId: 'EMP003',
          name: 'Saurav Kumar',
          email: 'saurav@aizops.com',
          phone: '+91 98765 43212',
          designation: 'Digital Marketing Specialist',
          department: 'Marketing',
          joiningDate: '2023-08-20',
          role: 'employee',
          status: 'active',
          salary: 550000,
          address: '789 Lake View, Bangalore',
          emergencyContact: '+91 98765 43213',
          bloodGroup: 'A+',
          dateOfBirth: '1996-11-08'
        },
        {
          id: 4,
          employeeId: 'EMP004',
          name: 'Sanjiv Singh',
          email: 'sanjiv@aizops.com',
          phone: '+91 98765 43213',
          designation: 'HR Manager',
          department: 'Human Resources',
          joiningDate: '2021-03-05',
          role: 'hr',
          status: 'active',
          salary: 900000,
          address: '321 Corporate Park, Pune',
          emergencyContact: '+91 98765 43214',
          bloodGroup: 'AB+',
          dateOfBirth: '1990-12-25'
        },
        {
          id: 5,
          employeeId: 'EMP005',
          name: 'Dr. Apj Abdul Kalam',
          email: 'kalam@aizops.com',
          phone: '+91 98765 43214',
          designation: 'Chief Scientist',
          department: 'R&D',
          joiningDate: '2023-01-15',
          role: 'employee',
          status: 'active',
          salary: 1500000,
          address: '555 Innovation Hub, Chennai',
          emergencyContact: '+91 98765 43215',
          bloodGroup: 'O-',
          dateOfBirth: '1931-10-15'
        }
      ];
      setEmployees(sampleEmployees);
      localStorage.setItem('employees', JSON.stringify(sampleEmployees));
    }
    setLoading(false);
  };

  const loadFeedbacks = () => {
    const savedFeedbacks = localStorage.getItem('employeeFeedbacks');
    if (savedFeedbacks) {
      setFeedbacks(JSON.parse(savedFeedbacks));
    } else {
      const sampleFeedbacks = [
        {
          id: 1,
          employeeId: 1,
          employeeName: 'Harsh Sharma',
          feedback: 'Excellent problem-solving skills and team collaboration',
          rating: 5,
          date: '2024-01-15',
          reviewer: 'Aman Verma'
        },
        {
          id: 2,
          employeeId: 2,
          employeeName: 'Aman Verma',
          feedback: 'Great leadership and project management skills',
          rating: 5,
          date: '2024-01-10',
          reviewer: 'Sanjiv Singh'
        }
      ];
      setFeedbacks(sampleFeedbacks);
      localStorage.setItem('employeeFeedbacks', JSON.stringify(sampleFeedbacks));
    }
  };

  const addEmployee = (newEmployee) => {
    const employee = {
      ...newEmployee,
      id: employees.length + 1,
      employeeId: `EMP${String(employees.length + 1).padStart(3, '0')}`,
      status: 'active'
    };
    const updatedEmployees = [...employees, employee];
    setEmployees(updatedEmployees);
    localStorage.setItem('employees', JSON.stringify(updatedEmployees));
    return true;
  };

  const updateEmployee = (updatedEmployee) => {
    const updatedEmployees = employees.map(emp => 
      emp.id === updatedEmployee.id ? updatedEmployee : emp
    );
    setEmployees(updatedEmployees);
    localStorage.setItem('employees', JSON.stringify(updatedEmployees));
  };

  const deleteEmployee = (id) => {
    if (window.confirm('Are you sure you want to delete this employee?')) {
      const updatedEmployees = employees.filter(emp => emp.id !== id);
      setEmployees(updatedEmployees);
      localStorage.setItem('employees', JSON.stringify(updatedEmployees));
      return true;
    }
    return false;
  };

  const addFeedback = (feedback) => {
    const newFeedback = {
      ...feedback,
      id: feedbacks.length + 1,
      date: new Date().toISOString().split('T')[0]
    };
    const updatedFeedbacks = [...feedbacks, newFeedback];
    setFeedbacks(updatedFeedbacks);
    localStorage.setItem('employeeFeedbacks', JSON.stringify(updatedFeedbacks));
    return true;
  };

  const canAddEmployee = ['hr', 'admin', 'superadmin'].includes(userRole);
  const canGiveFeedback = ['hr', 'admin', 'superadmin', 'manager'].includes(userRole);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Employee Management</h1>
        <p className="text-sm text-gray-500 mt-1">Manage employees, track performance, and provide feedback</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('list')}
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'list' 
              ? 'text-indigo-600 border-b-2 border-indigo-600' 
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          All Employees
        </button>
        {canAddEmployee && (
          <button
            onClick={() => setActiveTab('add')}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === 'add' 
                ? 'text-indigo-600 border-b-2 border-indigo-600' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Add Employee
          </button>
        )}
        <button
          onClick={() => setActiveTab('feedback')}
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'feedback' 
              ? 'text-indigo-600 border-b-2 border-indigo-600' 
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          {canGiveFeedback ? 'Give Feedback' : 'My Feedbacks'}
        </button>
      </div>

      {/* Content */}
      {activeTab === 'list' && (
        <EmployeeList 
          employees={employees} 
          userRole={userRole}
          onUpdate={updateEmployee}
          onDelete={deleteEmployee}
          feedbacks={feedbacks}
        />
      )}
      
      {activeTab === 'add' && canAddEmployee && (
        <AddEmployee onAdd={addEmployee} />
      )}
      
      {activeTab === 'feedback' && (
        <EmployeeFeedback 
          employees={employees}
          feedbacks={feedbacks}
          userRole={userRole}
          onAddFeedback={addFeedback}
        />
      )}
    </div>
  );
};

export default EmployeeManagement;